import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import Breadcrumb from '../components/Breadcrumb'
import KPICard from '../components/KPICard'
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'

function WellDetail() {
    const { theme } = useTheme()
    const { wellId } = useParams()
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        fetchData()
    }, [wellId])

    const fetchData = async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/dashboard/well?wellId=${encodeURIComponent(wellId)}`)
            if (!res.ok) throw new Error('Failed to fetch well data')
            setData(await res.json())
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    const well = data?.well || {}
    const production = data?.production || []
    const downtime = data?.downtime || []

    return (
        <div className="space-y-6">
            <Breadcrumb items={[
                { label: 'Production', path: '/production' },
                { label: well.unitName || `Well ${wellId}` }
            ]} />

            <div className={`border-b-2 ${theme === 'dark' ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <h1 className={`text-xl sm:text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                    <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                    {well.unitName || `Well ${wellId}`}
                </h1>
                {well.county && (
                    <p className={`${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 ml-3 sm:ml-4`}>
                        {well.county}, {well.state}
                    </p>
                )}
            </div>

            {error && (
                <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                    <p>{error}</p>
                </div>
            )}

            {data && (
                <>
                    {/* KPIs */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        <KPICard title="Oil (QTD)" value={Math.round(data.qtdOil || 0).toLocaleString()} unit="BBL" />
                        <KPICard title="Gas (QTD)" value={Math.round(data.qtdGas || 0).toLocaleString()} unit="MCF" />
                        <KPICard title="Water (QTD)" value={Math.round(data.qtdWater || 0).toLocaleString()} unit="BBL" />
                        <KPICard title="Uptime"
                            value={data.uptimePercent?.toFixed(1) || '0'} unit="%"
                            trend={data.uptimePercent >= 90 ? 'up' : data.uptimePercent < 70 ? 'down' : null} />
                    </div>

                    {/* Production Chart */}
                    {production.length > 0 && (
                        <div className="chart-container">
                            <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                                Daily Production (Last 90 Days)
                            </h2>
                            <div className="h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={production} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 12, fill: '#94a3b8' }}
                                            stroke="#475569"
                                            tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12, fill: '#94a3b8' }}
                                            stroke="#475569"
                                            tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#1e293b',
                                                border: '1px solid #475569',
                                                borderRadius: '8px',
                                                color: '#e2e8f0'
                                            }}
                                            formatter={(value) => value.toLocaleString()}
                                        />
                                        <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                                        <Line type="monotone" dataKey="oil" stroke="#22c55e" strokeWidth={2} dot={false} name="Oil (BBL)" />
                                        <Line type="monotone" dataKey="gas" stroke="#ef4444" strokeWidth={2} dot={false} name="Gas (MCF)" />
                                        <Line type="monotone" dataKey="water" stroke="#3b82f6" strokeWidth={2} dot={false} name="Water (BBL)" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    {/* Recent Downtime */}
                    {downtime.length > 0 && (
                        <div className="chart-container">
                            <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                                Recent Downtime
                            </h2>
                            <div className="overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th className="text-right">Hours</th>
                                            <th>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {downtime.map((evt, i) => (
                                            <tr key={i}>
                                                <td className="font-medium">
                                                    {evt.date ? new Date(evt.date).toLocaleDateString() : '--'}
                                                </td>
                                                <td className="text-right font-mono">{evt.hours?.toFixed(1) || '0'}</td>
                                                <td className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                                    {evt.reason || '--'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default WellDetail
